// THE IMPORT CONTROL EDITOR'S STATE, AS ONE VALUE (code review D-13,
// D-284).
//
// `EditableInvoiceForm` keeps this in one place and changes it only through
// `editorReducer`. It holds the latest value in a ref as well as rendering it,
// so a save started by a timer, a blur or a pick reads what is on screen NOW,
// never what a render's closure saw. Six mirror refs and a commit-tick effect
// did that job before, one per field, and each was a place for the two copies
// to disagree.
//
// No React and no I/O here: every rule below is a plain function, tested on its
// own in `app/mock/tests/invoice-editor-state.test.ts`.
import type { Invoice, IntakeSaveResult, InvoiceLineItem } from './types'
import { money2 } from './format'
import {
  headerStateFrom,
  lineChangeBaselineFrom,
  lineItemStateFrom,
  rowTotal,
  rowKey,
  type HeaderState,
  type LineItemState,
} from './intakeSaveBody'
import { applyToRows, combineRows, type BulkChoice, type CombineInput } from './lineItemBulk'
import { lineWriteSignature } from './saveOnBlur'

/**
 * One line's Additional Detail as the editor holds them (D-44, D-154). A
 * detail row is its own resource with its own `row_version` (`0` = "believed
 * not to exist yet"), written through its own route, never through the intake
 * save. The category labels are display text, refreshed the moment a pick is
 * made.
 */
export interface LineDetailState {
  marketingCategoryId: string | null
  marketingCategory: string | null
  marketingCategoryContext: string | null
  marketingRowVersion: number
  transportCategoryId: string | null
  transportCategory: string | null
  transportCategoryContext: string | null
  /** Fleet Maintenance (D-200): `null` until someone answers it. */
  transportFleetMaintenance: boolean | null
  transportRowVersion: number
}

export function detailStateFrom(c: InvoiceLineItem | undefined): LineDetailState {
  const md = c?.marketing_detail
  const td = c?.transportation_detail
  return {
    marketingCategoryId: md?.category_id ?? null,
    marketingCategory: md?.category ?? null,
    marketingCategoryContext: md?.category_context ?? null,
    marketingRowVersion: md?.row_version ?? 0,
    transportCategoryId: td?.category_id ?? null,
    transportCategory: td?.category ?? null,
    transportCategoryContext: td?.category_context ?? null,
    transportFleetMaintenance: td?.fleet_maintenance ?? null,
    transportRowVersion: td?.row_version ?? 0,
  }
}

export interface EditorState {
  header: HeaderState
  rows: readonly LineItemState[]
  /** Each line's Additional Detail, by line id. A line added this session has
      none until the save that creates it answers. */
  details: Readonly<Record<string, LineDetailState>>
  /** Saved lines the reviewer removed, sent with the next save (D-164). */
  removedIds: readonly string[]
  /** Edits on screen that no save has stored yet. */
  dirty: boolean
  /** Bumped by every edit. A save compares it with the value it started from:
      an edit typed while the request was out is not in what was stored, so
      the save must not clear `dirty` or release (QA Q11). */
  editSeq: number
  /** The invoice's concurrency counter, sent with every save. */
  rowVersion: number
  /** Each stored line's counter, by id — replaced by every save's answer, so a
      save queued behind another never sends a superseded one. */
  lineVersions: ReadonlyMap<string, number>
  /** Each stored line's last-saved write signature, by id: a save sends only
      the lines whose signature moved (C25). */
  baseline: ReadonlyMap<string, string>
  /** What is stored, after another person's save collided with this one. */
  serverRow: Invoice | null
  /** The last temporary id handed to an added line. */
  newIds: number
}

export function initialEditorState(invoice: Invoice, lineItems: readonly InvoiceLineItem[]): EditorState {
  return {
    header: headerStateFrom(invoice),
    rows: lineItems.map(lineItemStateFrom),
    details: Object.fromEntries(lineItems.map((c) => [c.id, detailStateFrom(c)])),
    removedIds: [],
    dirty: false,
    editSeq: 0,
    rowVersion: invoice.row_version,
    lineVersions: new Map(lineItems.map((c) => [c.id, c.row_version])),
    baseline: lineChangeBaselineFrom(lineItems),
    serverRow: null,
    newIds: 0,
  }
}

/** What one save sent, frozen when it started. */
export interface SaveSnapshot {
  header: HeaderState
  rows: readonly LineItemState[]
  removedIds: readonly string[]
  editSeq: number
  /** The rows the save wrote (`changedLineIds`). */
  changedIds: ReadonlySet<string>
}

export type EditorAction =
  | { type: 'header'; patch: Partial<HeaderState> }
  | { type: 'row'; id: string; patch: Partial<LineItemState> }
  /** Price or Quantity typed: the row's Total follows (D-4, code review D-02). */
  | { type: 'amounts'; id: string; price?: string; quantity?: string }
  | { type: 'addRow' }
  | { type: 'removeRow'; id: string }
  | { type: 'applyToRows'; ids: ReadonlySet<string>; choice: BulkChoice }
  | { type: 'combine'; ids: ReadonlySet<string>; input: CombineInput }
  /** A category or Fleet Maintenance chosen. Saved on its own route, so it is
      not an edit of the intake save's. */
  | { type: 'detail'; id: string; patch: Partial<LineDetailState> }
  | { type: 'detailSaved'; id: string; lineItem: InvoiceLineItem }
  | { type: 'saved'; sent: SaveSnapshot; result: IntakeSaveResult; focused: ReadonlySet<string> }
  /** Take the invoice's counter from a 409, with what is stored when it was
      somebody else's write, or none when it was this form's own. */
  | { type: 'conflict'; rowVersion: number; serverRow: Invoice | null }
  /** The invoice left this step while the reviewer typed: what they typed
      since can no longer be stored here, so nothing is pending. */
  | { type: 'discard' }

/** Every action an edit: marks the form dirty and bumps the counter. */
function edited(state: EditorState, next: Partial<EditorState>): EditorState {
  return { ...state, ...next, dirty: true, editSeq: state.editSeq + 1 }
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'header':
      return edited(state, { header: { ...state.header, ...action.patch } })
    case 'row':
      return edited(state, {
        rows: state.rows.map((r) => (r.lineItemId === action.id ? { ...r, ...action.patch } : r)),
      })
    case 'amounts':
      return edited(state, {
        rows: state.rows.map((r) => {
          if (r.lineItemId !== action.id) return r
          const price = action.price ?? r.price
          const quantity = action.quantity ?? r.quantity
          return { ...r, price, quantity, total: rowTotal(r, price, quantity) }
        }),
      })
    case 'addRow': {
      const newIds = state.newIds + 1
      const sequence = state.rows.reduce((m, r) => Math.max(m, r.sequence ?? 0), 0) + 1
      return edited(state, {
        newIds,
        rows: [
          ...state.rows,
          { lineItemId: `new-${newIds}`, clientKey: `new-${newIds}`, sequence, description: '', quantity: '', price: '', total: '', isNew: true },
        ],
      })
    }
    case 'removeRow': {
      const row = state.rows.find((r) => r.lineItemId === action.id)
      return edited(state, {
        rows: state.rows.filter((r) => r.lineItemId !== action.id),
        removedIds: row && !row.isNew ? [...state.removedIds, action.id] : state.removedIds,
      })
    }
    case 'applyToRows':
      return edited(state, { rows: applyToRows(state.rows, action.ids, action.choice) })
    case 'combine': {
      const newIds = state.newIds + 1
      const result = combineRows(state.rows, action.ids, action.input, `new-${newIds}`)
      return edited(state, {
        newIds,
        rows: result.rows,
        removedIds: [...state.removedIds, ...result.removedIds],
      })
    }
    case 'detail': {
      const d = state.details[action.id] ?? detailStateFrom(undefined)
      return { ...state, details: { ...state.details, [action.id]: { ...d, ...action.patch } } }
    }
    case 'detailSaved':
      return { ...state, details: { ...state.details, [action.id]: detailStateFrom(action.lineItem) } }
    case 'saved':
      return adoptSave(state, action.sent, action.result, action.focused)
    case 'conflict':
      return { ...state, rowVersion: action.rowVersion, serverRow: action.serverRow }
    case 'discard':
      return { ...state, dirty: false }
  }
}

/**
 * Pair each row a save SENT with the stored line it became. A row that went
 * with an id keeps it. A row that went in new is paired among the lines this
 * save created and nothing else: the stored lines no sent row names and no
 * sent removal names. By its line number first; a created line whose number
 * the server moved (a combined line skips a number a removed line still
 * reserves) is paired with the new rows left over, in order. Until
 * 2026-10-03 the pool also held a line removed while the save was out, and a
 * row added since took that line's id (code review of D-284).
 */
export function pairSentToStored(
  sent: readonly LineItemState[],
  stored: readonly InvoiceLineItem[],
  removed: readonly string[] = [],
): Map<string, InvoiceLineItem> {
  const named = new Set([...sent.filter((r) => !r.isNew).map((r) => r.lineItemId), ...removed])
  const created = stored.filter((c) => !named.has(c.id))
  const out = new Map<string, InvoiceLineItem>()
  for (const r of sent) {
    if (r.isNew) continue
    const match = stored.find((c) => c.id === r.lineItemId)
    if (match) out.set(r.lineItemId, match)
  }
  const fresh = sent.filter((r) => r.isNew)
  const unpaired: LineItemState[] = []
  for (const r of fresh) {
    const at = r.sequence == null ? -1 : created.findIndex((c) => c.line_number === r.sequence)
    if (at < 0) unpaired.push(r)
    else out.set(r.lineItemId, created.splice(at, 1)[0]!)
  }
  for (const r of unpaired) {
    const next = created.shift()
    if (next) out.set(r.lineItemId, next)
  }
  return out
}

/**
 * A SAVE'S ANSWER, TAKEN BACK. The counters always: the invoice's, every
 * stored line's, and the baseline of each line this save wrote. Then one of
 * two things:
 *
 * - NOTHING TYPED SINCE IT STARTED: the stored values replace what is on
 *   screen — except in a field that has focus right now, whose text is
 *   already exactly what was sent, so a reseed would only move the caret
 *   (`money2` turning "50" into "50.00" under it, C21).
 * - SOMETHING TYPED SINCE: every value on screen stays, the form stays dirty,
 *   and only identity is taken — a line this save created now has its id, so
 *   the next save updates it rather than creating a second copy.
 *
 * Either way the removals this save carried are done, and one made since is not.
 */
function adoptSave(
  state: EditorState,
  sent: SaveSnapshot,
  result: IntakeSaveResult,
  focused: ReadonlySet<string>,
): EditorState {
  const clean = state.editSeq === sent.editSeq
  const stored = result.line_items
  // The pairing is the SENT rows' (code review of D-284): a row added or a line
  // removed while the save was out took no part in it.
  const pairing = stored ? pairSentToStored(sent.rows, stored, sent.removedIds) : new Map<string, InvoiceLineItem>()
  let details = state.details
  let lineVersions = state.lineVersions
  let baseline = state.baseline
  if (stored) {
    // A line this save created has detail rows now — a combined line carries
    // its lines' agreed categories (D-230).
    const fresh = stored.filter((c) => !(c.id in details))
    if (fresh.length > 0) details = { ...details, ...Object.fromEntries(fresh.map((c) => [c.id, detailStateFrom(c)])) }
    lineVersions = new Map([...lineVersions, ...stored.map((c) => [c.id, c.row_version] as const)])
    const nextBaseline = new Map(baseline)
    for (const r of sent.rows) {
      if (!sent.changedIds.has(r.lineItemId)) continue
      const line = pairing.get(r.lineItemId)
      if (line) nextBaseline.set(line.id, lineWriteSignature(r))
    }
    baseline = nextBaseline
  }
  const removedIds = state.removedIds.filter((id) => !sent.removedIds.includes(id))
  const base = { ...state, details, lineVersions, baseline, removedIds, rowVersion: result.invoice.row_version, serverRow: null }
  if (!clean) {
    return stored ? { ...base, rows: adoptLineIdentity(state.rows, pairing, stored) } : base
  }
  return {
    ...base,
    dirty: false,
    header: reseedHeader(state.header, result.invoice, focused),
    rows: stored ? reseedRows(state.rows, pairing, focused) : state.rows,
  }
}

/** The header as stored, keeping the text of a focused field. */
function reseedHeader(prev: HeaderState, saved: Invoice, focused: ReadonlySet<string>): HeaderState {
  const keep = (key: string) => focused.has(key)
  const dateDefault = (saved.invoice_date ?? '').slice(0, 10)
  return {
    externalinvoiceid: keep('externalinvoiceid') ? prev.externalinvoiceid : (saved.external_invoice_id ?? dateDefault),
    // The seeded default, kept only while the server still holds no number; a
    // field being typed in keeps what it had.
    ...(keep('externalinvoiceid')
      ? prev.externalInvoiceIdDefault !== undefined
        ? { externalInvoiceIdDefault: prev.externalInvoiceIdDefault }
        : {}
      : saved.external_invoice_id == null
        ? { externalInvoiceIdDefault: dateDefault }
        : {}),
    date: keep('date') ? prev.date : saved.invoice_date ? saved.invoice_date.slice(0, 10) : '',
    subtotal: saved.subtotal != null ? money2(String(saved.subtotal)) : '',
    tax: keep('tax') ? prev.tax : saved.tax != null ? money2(String(saved.tax)) : '0.00',
    shipping: keep('shipping') ? prev.shipping : saved.shipping != null ? money2(String(saved.shipping)) : '0.00',
    total: keep('total') ? prev.total : saved.total != null ? money2(String(saved.total)) : '',
    currency: saved.currency ?? 'USD',
    vendorId: saved.vendor_id || undefined,
    vendorName: saved.vendor_name || undefined,
    // The invoice's own company, which the save writes (code review B-05).
    companyId: saved.company_id || undefined,
    companyName: saved.company_name || undefined,
    manualEntry: saved.manual_entry_requested ?? false,
  }
}

/** Every row as stored, keeping the text of a focused Description, Quantity or
    Price, and its key on screen. */
function reseedRows(
  rows: readonly LineItemState[],
  pairing: ReadonlyMap<string, InvoiceLineItem>,
  focused: ReadonlySet<string>,
): LineItemState[] {
  return rows.map((prev) => {
    const line = pairing.get(prev.lineItemId)
    if (!line) return prev
    const key = rowKey(prev)
    const fresh = { ...lineItemStateFrom(line), clientKey: key }
    const keepDescription = focused.has(`description:${key}`)
    const keepQuantity = focused.has(`quantity:${key}`)
    const keepPrice = focused.has(`price:${key}`)
    if (!keepDescription && !keepQuantity && !keepPrice) return fresh
    const description = keepDescription ? prev.description : fresh.description
    const quantity = keepQuantity ? prev.quantity : fresh.quantity
    const price = keepPrice ? prev.price : fresh.price
    return { ...fresh, description, quantity, price, total: rowTotal(fresh, price, quantity) }
  })
}

/** The stored lines' ids and counters, every value on screen kept. Only a row
    that went out new takes an id; a row added since stays new. */
export function adoptLineIdentity(
  rows: readonly LineItemState[],
  pairing: ReadonlyMap<string, InvoiceLineItem>,
  stored: readonly InvoiceLineItem[],
): LineItemState[] {
  return rows.map((r) => {
    if (r.isNew) {
      const c = pairing.get(r.lineItemId)
      return c ? { ...r, clientKey: rowKey(r), lineItemId: c.id, rowVersion: c.row_version, isNew: false } : r
    }
    const c = stored.find((x) => x.id === r.lineItemId)
    return c ? { ...r, rowVersion: c.row_version } : r
  })
}
